import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { FaPrint, FaFileExcel, FaCopy } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const AccountantSubeShifarasList = () => {
  const [loading, setLoading] = useState(true);
  const [worksData, setWorksData] = useState([]);
  const [availableTalukas, setAvailableTalukas] = useState([]);
  
  const [selectedTaluka, setSelectedTaluka] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch Data from generatework
  useEffect(() => {
    const listRef = ref(db, 'ekamwatapzp/generatework');
    
    const unsub = onValue(listRef, (snapshot) => {
      if (snapshot.exists()) {
        const rawData = snapshot.val();
        let flattenedWorks = [];
        let talukaSet = new Set();

        // Data is nested: generatework -> dateStr -> randomPushId -> workData
        Object.values(rawData).forEach(dateGroup => {
          if(dateGroup && typeof dateGroup === 'object') {
             Object.entries(dateGroup).forEach(([key, work]) => {
                if(work && typeof work === 'object' && work.sharedwork === 'सुबे') {
                    const taluka = work.taluka ? work.taluka.trim() : 'Unknown';
                    talukaSet.add(taluka);
                    flattenedWorks.push({
                        id: key,
                        workNumber: work.workNumber || '-',
                        taluka: taluka,
                        workName: work.workName || '-',
                        duration: work.monthPeriod || work.duration || '',
                        adminApproval: work.adminApproval || '',
                        techApproval: work.techApproval || '',
                        amount: work.tenderAmount || '0',
                        schemeName: work.schemeName || work.category || '-',
                        remark: work.remark || ''
                    });
                }
             })
          }
        });

        const sortedTalukas = Array.from(talukaSet).sort();
        setAvailableTalukas(sortedTalukas);
        if(sortedTalukas.length > 0) {
            setSelectedTaluka(sortedTalukas[0]);
        }
        setWorksData(flattenedWorks);
      } else {
        setWorksData([]);
        setAvailableTalukas([]);
        setSelectedTaluka('');
      }
      setLoading(false);
    });

    return () => unsub();
  }, []);

  // Filter the list by exactly matching the Taluka & fuzzy matching Search
  const filteredData = worksData.filter((work) => {
    if (selectedTaluka && work.taluka !== selectedTaluka) return false;
    
    if (searchTerm) {
        const q = searchTerm.toLowerCase();
        return (
            String(work.workNumber).toLowerCase().includes(q) ||
            String(work.workName).toLowerCase().includes(q) ||
            String(work.schemeName).toLowerCase().includes(q)
        );
    }
    return true;
  });

  const handlePrint = () => {
      window.print();
  }

  const exportToExcel = () => {
      const exportData = filteredData.map((w, i) => ({
          'अ.क्र': i + 1,
          'वर्क नंबर': w.workNumber,
          'तालुका': w.taluka,
          'कामाचे नाव': w.workName,
          'कामाची मुदत (महिने)': w.duration,
          'प्रशासकीय मंजुरी क्र. व दिनांक': w.adminApproval,
          'तांत्रिक मंजूरी क्र. व दिनांक': w.techApproval,
          'र.रु. (लाखात)': w.amount,
          'लेखाशिर्षक / योजनेचे नाव': w.schemeName,
          'शेरा': w.remark
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Shifaras List");
      XLSX.writeFile(wb, `Sube_Shifaras_List_${selectedTaluka || 'All'}.xlsx`);
  }

  const copyToClipboard = () => {
      const text = filteredData.map(w => `${w.workNumber}\t${w.workName}\t${w.amount}`).join('\n');
      navigator.clipboard.writeText(text);
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[98%] mx-auto bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        
        {/* Header Section */}
        <div className="bg-[#ecedf3] text-gray-800 p-4 border-b border-gray-300 flex justify-between items-center print:hidden">
          <h1 className="text-xl font-medium">सु.बे. शिफारस यादी</h1>
          <h2 className="text-sm font-medium text-gray-500">ठाणे जिल्हा परिषद / सु.बे. यादी</h2>
        </div>

        {/* Inputs / Filters */}
        <div className="p-6 bg-white print:hidden">
          <div className="flex flex-col gap-2 max-w-xs mb-8">
            <label className="text-gray-700 font-medium text-sm">
              तालुका निवडा <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-row gap-4 w-[400px]">
              <div className="relative inline-block w-64">
                <select
                  value={selectedTaluka}
                  onChange={(e) => setSelectedTaluka(e.target.value)}
                  className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 pr-8 rounded focus:outline-none focus:border-blue-500 appearance-none text-sm h-10"
                >
                  <option value="">तालुका निवडा</option>
                  {availableTalukas.map(taluka => (
                      <option key={taluka} value={taluka}>{taluka}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
              <button className="bg-[#5c6bc0] hover:bg-blue-600 text-white text-sm font-medium px-6 rounded h-10 transition-colors">
                Search
              </button>
            </div>
          </div>

          {/* Action Buttons & Quick Search */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex rounded-md shadow-sm">
              <button onClick={copyToClipboard} className="bg-gray-600 hover:bg-gray-700 text-white text-sm px-4 py-2 border-r border-gray-500 rounded-l-md transition-colors flex items-center gap-2">
                Copy
              </button>
              <button onClick={exportToExcel} className="bg-gray-600 hover:bg-gray-700 text-white text-sm px-4 py-2 border-r border-gray-500 transition-colors flex items-center gap-2">
                Excel
              </button>
              <button onClick={handlePrint} className="bg-gray-600 hover:bg-gray-700 text-white text-sm px-4 py-2 rounded-r-md transition-colors flex items-center gap-2">
                Print
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Search:</label>
              <input 
                type="text" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500 w-48"
              />
            </div>
          </div>
        </div>

        {/* Table Print View Wrapper */}
        <div className="p-0 overflow-x-auto border border-gray-200">
          <table className="w-full border-collapse bg-white text-sm print:text-[10px]">
            <thead>
              {/* Top Header Group */}
              <tr>
                <th colSpan={10} className="border-b border-gray-400 py-4 text-center text-gray-800 font-bold bg-white">
                  बांधकाम उपविभाग पंचायत समिती{selectedTaluka ? ` ${selectedTaluka}` : ''}
                </th>
              </tr>
              {/* Second Level Header Group */}
              <tr>
                <th colSpan={7} className="border-b border-r border-gray-400 py-3 text-center text-gray-800 font-bold bg-white">
                  जिल्हा परिषद काम वाटप समिती पुढे शिफारस करावयाच्या यादी दि.
                </th>
                <th colSpan={3} className="border-b border-gray-400 py-3 text-center text-gray-800 font-bold bg-white">
                  रोजी घ्यावयाची कामे
                </th>
              </tr>
              {/* Specific Data Columns */}
              <tr className="bg-gray-50 text-gray-800 text-[13px] print:text-[10px] border-b border-gray-400 leading-tight text-center">
                <th className="px-2 py-4 border-r border-gray-400 w-16">अ.क्र</th>
                <th className="px-3 py-4 border-r border-gray-400 w-20">वर्क<br/>नंबर</th>
                <th className="px-3 py-4 border-r border-gray-400 w-24">तालुका</th>
                <th className="px-4 py-4 border-r border-gray-400 min-w-[200px] text-left">कामाचे नाव</th>
                <th className="px-3 py-4 border-r border-gray-400 w-28">कामाची मुदत<br/>(महिने)</th>
                <th className="px-3 py-4 border-r border-gray-400 w-36">प्रशासकीय मंजुरी<br/>क्र. व दिनांक</th>
                <th className="px-3 py-4 border-r border-gray-400 w-36">तांत्रिक मंजूरी<br/>क्र. व दिनांक</th>
                <th className="px-3 py-4 border-r border-gray-400 w-24">र.रु.<br/>(लाखात)</th>
                <th className="px-4 py-4 border-r border-gray-400 min-w-[200px] text-left">लेखाशिर्षक / योजनेचे नाव</th>
                <th className="px-3 py-4 w-20 text-left pl-3">शेरा</th>
              </tr>
            </thead>
            <tbody className="text-gray-800 bg-white">
              {loading ? (
                <tr>
                    <td colSpan="10" className="text-center py-10 font-bold text-blue-500 animate-pulse">डेटा लोड होत आहे...</td>
                </tr>
              ) : filteredData.length > 0 ? (
                filteredData.map((row, index) => (
                    <tr key={row.id} className="border-b border-gray-300 hover:bg-gray-50 transition-colors">
                      <td className="px-2 py-4 text-center border-r border-gray-300 font-medium">{index + 1}</td>
                      <td className="px-3 py-4 text-center border-r border-gray-300 font-bold">{row.workNumber}</td>
                      <td className="px-3 py-4 text-center border-r border-gray-300">{row.taluka}</td>
                      <td className="px-4 py-4 text-left border-r border-gray-300 font-semibold">{row.workName}</td>
                      <td className="px-3 py-4 text-center border-r border-gray-300">{row.duration || '-'}</td>
                      <td className="px-3 py-4 text-center border-r border-gray-300">{row.adminApproval || '-'}</td>
                      <td className="px-3 py-4 text-center border-r border-gray-300">{row.techApproval || '-'}</td>
                      <td className="px-3 py-4 text-right border-r border-gray-300 font-bold">{row.amount}</td>
                      <td className="px-4 py-4 text-left border-r border-gray-300 font-medium">{row.schemeName}</td>
                      <td className="px-3 py-4 text-left">{row.remark || '-'}</td>
                    </tr>
                ))
              ) : (
                <tr>
                    <td colSpan="10" className="text-center py-10 text-gray-500 font-medium">या तालुक्यासाठी कोणतीही कामे उपलब्ध नाहीत.</td>
                </tr>
              )}
            </tbody>
          </table>
          
          <style dangerouslySetInnerHTML={{ __html: `
            @media print {
              body * { visibility: hidden; }
              .overflow-x-auto, .overflow-x-auto * { visibility: visible; }
              .overflow-x-auto { position: absolute; left: 0; top: 0; width: 100%; }
              table { width: 100% !important; margin: 0 !important; }
              th, td { border: 1px solid black !important; padding: 4px !important; }
            }
          `}} />
        </div>

      </div>
    </div>
  );
};

export default AccountantSubeShifarasList;
